import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import type { MediaItem } from '../types'

/** Fullscreen viewer with prev/next + keyboard nav. Videos use the HLS
 *  playlist (native playback on Safari/iOS; elsewhere falls back to poster). */
export function Lightbox({
  items,
  index,
  onClose,
  onIndex,
}: {
  items: MediaItem[]
  index: number
  onClose: () => void
  onIndex: (i: number) => void
}) {
  const item = items[index]
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') onIndex(Math.min(items.length - 1, index + 1))
      else if (e.key === 'ArrowLeft') onIndex(Math.max(0, index - 1))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, items.length, onClose, onIndex])

  if (!item) return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/90 p-4"
      onClick={onClose}
    >
      <button
        className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full border border-edge bg-ink/70 font-comic text-2xl text-white shadow-panel backdrop-blur transition hover:border-pow hover:text-pow hover:shadow-neon-pink"
        onClick={onClose}
        aria-label="Close"
      >
        ✕
      </button>
      {index > 0 ? (
        <button
          className="absolute left-3 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border border-edge bg-ink/70 text-3xl text-white shadow-panel backdrop-blur transition hover:border-kapow hover:text-kapow hover:shadow-neon-cyan"
          onClick={(e) => {
            e.stopPropagation()
            onIndex(index - 1)
          }}
          aria-label="Previous"
        >
          ‹
        </button>
      ) : null}
      {index < items.length - 1 ? (
        <button
          className="absolute right-3 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border border-edge bg-ink/70 text-3xl text-white shadow-panel backdrop-blur transition hover:border-kapow hover:text-kapow hover:shadow-neon-cyan"
          onClick={(e) => {
            e.stopPropagation()
            onIndex(index + 1)
          }}
          aria-label="Next"
        >
          ›
        </button>
      ) : null}

      <div className="max-h-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        {item.kind === 'video' && item.playlistUrl ? (
          <video
            key={item.id}
            src={item.playlistUrl}
            poster={item.src || undefined}
            controls
            autoPlay
            playsInline
            className="max-h-[85vh] w-full rounded-lg border border-edge bg-black shadow-panel-lg"
          />
        ) : (
          <img
            src={item.fullsize ?? item.src}
            alt={item.alt ?? ''}
            className="max-h-[85vh] w-full rounded-lg border border-edge object-contain shadow-panel-lg"
          />
        )}
        {item.authorHandle ? (
          <div className="mt-2 text-center text-sm text-muted">
            @{item.authorHandle} · #{item.tag}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
